import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { updateSuccessCriteria } from './api';
import { useClient } from 'shared/apollo/hooks';
import { IndicatorSectionKey, IndicatorWithUpdate } from './utils';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import {
  ErrorButton,
  SavedButton,
  SavingButton,
} from '../IndicatorDetails/IndicatorBottomButtons';
import { RequestState } from 'shared/auth/hooks/useRequestState';
import IndicatorTextareaRender from './IndicatorTextareaRender';
import {
  getIndicatorType,
  ghostText,
} from '../ImpactSummary/IndicatorGhostText';

const placeholderText = (
  indicator: IndicatorWithUpdate,
  sectionKey: IndicatorSectionKey,
  programName: string | undefined,
  beneficiaryTerm: string | undefined,
  intermediaries: string | undefined,
  serviceGoal: string | undefined,
  serviceType: string | undefined,
): string | undefined => {
  if (indicator.id === 10032) {
    return `Define how an individual ${(
      intermediaries || beneficiaryTerm
    )?.toLowerCase()} must engage with ${programName} to be considered successfully reached.`;
  }
  if (!indicator.isHuman) {
    return `Define what ${indicator.name.toLowerCase()} means to ${programName}`;
  }
  const type = getIndicatorType(indicator);
  const text = ghostText(
    indicator,
    programName || 'your program',
    beneficiaryTerm?.toLowerCase(),
    intermediaries?.toLowerCase(),
    serviceGoal,
    serviceType,
  );
  return text[type][sectionKey];
  // const beneficiaryTermLowerCase = beneficiaryTerm?.toLowerCase();
  // switch (sectionKey) {
  //   case 'capacityDevelopment':
  //     return 'Briefly define the programmatic changes.';
  //   case 'reach':
  //     return `Define how individual ${beneficiaryTermLowerCase} must engage with ${programName} to be considered successfully reached.`;
  //   case 'learn':
  //     return `Briefly define what successful ${beneficiaryTermLowerCase} learn from this program.`;
  //   case 'act':
  //     return 'Briefly define these successful actions or behaviors.';
  //   case 'succeed':
  //     return 'Briefly define the success threshold for this outcome.';
  //   default:
  //     return 'Please define what this indicator means to your program.';
  // }
};

export const GetSaveStatus: React.FC<{
  state: RequestState<unknown>;
  className?: string;
}> = ({ state, className }) => {
  const [show, setShow] = useState<boolean>(true);

  useEffect(() => {
    setShow(true);
  }, [state.type, setShow]);

  switch (state.type) {
    case 'REQUEST_START':
      return <SavingButton isButton={false} className={className} />;

    case 'REQUEST_SUCCESS':
      if (!show) return null;
      window.setTimeout(() => setShow(false), 2000);
      return <SavedButton isButton={false} className={className} />;

    case 'REQUEST_ERROR':
      return (
        <ErrorButton
          text="Unable to save. Please try again."
          isButton={false}
          className={className}
        />
      );

    default:
      return null;
  }
};

export interface IndicatorTextareaProps {
  sectionKey: IndicatorSectionKey;
  indicator: IndicatorWithUpdate;
  reportId: number;
  intermediaries: string | undefined;
  programName: string | undefined;
  beneficiaryTerm: string | undefined;
  serviceGoal: string | undefined;
  serviceType: string | undefined;
  index: number;
  handleIndicatorTextUpdate: (
    sectionKey: IndicatorSectionKey,
    text: string,
    index: number,
  ) => unknown;
}

export const StyledTrueImpactText = styled(TrueImpactText)<{
  textareaBackgroundColor: keyof typeof COLORS;
}>`
  position: relative;
  margin-bottom: 20px;
  & .textarea-error {
    border: 1px solid ${COLORS.critical};
  }
  textarea {
    width: 100%;
    min-height: 140px;
    border: none;
    resize: vertical;
    background-color: ${({ textareaBackgroundColor }) =>
      COLORS[textareaBackgroundColor]};
    border-radius: 4px;
    background: ${COLORS.whiteBase};
    border: 1px solid ${COLORS.greyBase};
    text-align: left;
    font-size: 16px;
    max-height: 500px;
    border-radius: 4px;
    color: ${COLORS.blackBase};
    padding: ${SPACERS.small};
    &[disabled] {
      background: ${COLORS.grayLight};
    }
    ::placeholder {
      color: ${COLORS.grayBase};
      opacity: 1;
    }

    ::-ms-input-placeholder {
      color: ${COLORS.grayBase};
    }
  }
  .status-badge {
    position: absolute;
    left: 50%;
    bottom: ${SPACERS.tiny};
    padding-left: ${SPACERS.tiny};
    padding-right: ${SPACERS.tiny};
    background-color: ${COLORS.whiteBase};
    transform: translate(-50%);
  }
`;

const IndicatorTextarea: React.FC<IndicatorTextareaProps & {
  textareaBackgroundColor?: keyof typeof COLORS;
  className?: string;
}> = ({
  sectionKey,
  index,
  indicator,
  reportId,
  className,
  beneficiaryTerm,
  intermediaries,
  serviceGoal,
  serviceType,
  programName,
  textareaBackgroundColor = 'whiteBase',
  handleIndicatorTextUpdate,
}) => {
  const client = useClient();
  const [error, setError] = useState<string | undefined>(undefined);
  const [addSuccessCriteria, makeRequest] = useGraphQLRequest<unknown>();

  const handleSave = useCallback(
    (text: string) => {
      handleIndicatorTextUpdate(sectionKey, text, index);
      if (!text || !handleIndicatorTextUpdate) {
        setError(
          'Unable to save changes. Please provide a valid text to update.',
        );
      } else {
        makeRequest(
          updateSuccessCriteria(client!, reportId, indicator.id, true, text),
        );
        setError(undefined);
      }
    },
    [indicator.id, index, sectionKey, handleIndicatorTextUpdate, setError],
  );

  useEffect(() => {
    if (indicator.customizable) return;
    handleIndicatorTextUpdate(sectionKey, indicator.definition, index);
  }, [sectionKey, indicator.customizable, index]);

  if (!indicator.customizable) return null;

  return (
    <StyledTrueImpactText
      variant="body"
      className={className}
      textareaBackgroundColor={textareaBackgroundColor}
    >
      <IndicatorTextareaRender
        id={indicator.id}
        disabled={!indicator.customizable}
        handleSave={handleSave}
        initialValue={indicator.updatedText}
        placeholder={placeholderText(
          indicator,
          sectionKey,
          programName,
          beneficiaryTerm,
          intermediaries,
          serviceGoal,
          serviceType,
        )}
        textareaClassName={
          error || addSuccessCriteria.type === 'REQUEST_ERROR'
            ? 'textarea-error'
            : undefined
        }
      />
      <GetSaveStatus state={addSuccessCriteria} className="status-badge" />
    </StyledTrueImpactText>
  );
};

export default IndicatorTextarea;
